// Downloads a (possibly cross-origin, presigned) file URL by fetching it into
// a blob and clicking a synthetic <a download> — a plain <a download> silently
// degrades to just navigating there instead of downloading on most browsers,
// since the download attribute is only honored same-origin (or when the
// response itself sets Content-Disposition: attachment, which this may not).
//
// `onProgress(0..100)` is optional and reports real bytes-received progress
// by reading the body stream by hand instead of awaiting response.blob()
// directly — only possible when the response carries a Content-Length (true
// for the presigned file URLs this is used for); otherwise progress is
// simply never reported and the caller should treat it as indeterminate.
export async function downloadFileFromUrl(url, filename, onProgress) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("download failed");

    const total = Number(response.headers.get("content-length")) || 0;
    let blob;
    if (onProgress && total > 0 && response.body) {
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        onProgress(Math.min(100, Math.round((received / total) * 100)));
      }
      blob = new Blob(chunks);
    } else {
      blob = await response.blob();
    }

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Most likely a CORS-blocked fetch (the file host allows direct
    // navigation but not cross-origin reads) — fall back to opening it in a
    // new tab so the user can still save it via the browser's own "Save
    // video as", rather than a silent dead click.
    window.open(url, "_blank", "noopener");
  }
}
